import { z } from 'zod';
import { FEATURES, LICENSE_PAYMENT_METHODS } from '../constants/license.js';
import { isoDate, objectId, optional, reason, text } from './common.js';

export const limitsSchema = z.strictObject({
  maxOutlets: z.coerce.number().int().min(1).max(1000),
  maxDevices: z.coerce.number().int().min(1).max(10000),
  maxUsers: z.coerce.number().int().min(1).max(10000),
});

export const featuresSchema = z.strictObject(
  Object.fromEntries(
    FEATURES.map((f) => [
      f.key,
      // Coming-soon features can't be switched on until they ship.
      f.comingSoon
        ? z.literal(false, { error: `${f.label} is coming in the next update` }).default(false)
        : z.boolean().default(f.default ?? false),
    ]),
  ),
);

export const graceDays = z.coerce.number().int().min(0).max(90);

export const licensePaymentSchema = z.strictObject({
  amountPaise: z.coerce.number().int().min(0).max(10_000_000_00),
  paidOn: isoDate,
  method: z.enum(LICENSE_PAYMENT_METHODS),
  reference: optional(text(100)),
  invoiceNo: optional(text(40)),
  periodFrom: optional(isoDate),
  periodTo: optional(isoDate),
  notes: optional(text(500)),
});

export const extendLicenseSchema = z
  .strictObject({
    newExpiryDate: isoDate.optional(),
    days: z.coerce.number().int().min(1).max(3650).optional(),
    reason,
  })
  .refine((v) => Boolean(v.newExpiryDate) !== Boolean(v.days), {
    message: 'Give either a new expiry date or a number of days',
    path: ['days'],
  });

export const renewLicenseSchema = z.strictObject({
  months: z.coerce.number().int().min(1).max(60),
  planId: objectId.optional(),
  applyPlanDefaults: z.boolean().default(false),
  reason,
  payment: licensePaymentSchema.optional(),
});

export const changePlanSchema = z.strictObject({
  planId: objectId,
  applyPlanDefaults: z.boolean().default(true),
  reason,
});

export const updateLimitsSchema = z.strictObject({ limits: limitsSchema, reason });
export const updateFeaturesSchema = z.strictObject({ features: featuresSchema, reason });
export const updateGraceSchema = z.strictObject({ graceDays, reason });
export const suspendSchema = z.strictObject({ reason });
export const reactivateSchema = z.strictObject({ reason });
